(ns admin.containers.curriculum.reports-readiness.curriculum-learners-readiness
  (:require [applied-science.js-interop :as j]
            [cemerick.url :refer [map->query]]
            [kyber.components.composite.expander :as expander]
            [kyber.components.composite.diff-badge :as diff-badge]
            [kyber.components.base.button :as button]
            [kyber.components.base.link :as link]
            [kyber.components.base.text :as text]
            [kyber.styles.core :refer [gs styles->classes]]
            [kyber.utils :as ku]
            [admin.router.utils :as aru]
            [clojure.set :as set]
            [kyber.components.composite.pagination-controls :as pagination-controls]
            [admin.events.containers.curriculum-reports :as curriculum-reports]
            [kyber.constants :as kc]
            [re-frame.core :as re-frame]
            [kyber.components.base.icons :as icon]
            [clojure.string :as str]))

(def classes
  (styles->classes
   {:container
    {:border           (str "1px solid"
                            (gs [:colors :gray-scale-200]))
     :border-radius    (gs [:radius :r3])
     :background-color (gs [:colors :true-white])}

    :learner-items
    {"& > li"
     {:border-top
      (str "1px solid"
           (gs [:colors :gray-scale-200]))}
     "& li:first-child"
     {:border-top :none}}

    :subtitle
    {:font-style "italic"
     :font-size "0.85em"}

    :title
    {:font-weight (gs [:font-weight :title-10])
     :color (gs [:colors :gray-scale-600])
     :font-size "1em"
     :font-family (gs [:font-family :open-sans])}

    :is-supplemental
    {:border-radius (gs [:radius :r3])
     :background-color (gs [:colors :gray-scale-200])
     :font-size (gs [:font-size :caption-10])
     :text-transform :uppercase
     :padding [(gs [:spacing :p4])
               (gs [:spacing :p8])]
     :margin-right (gs [:spacing :p12])}

    :dot
    {:width         (gs [:spacing :p12])
     :height        (gs [:spacing :p12])
     :border-radius (gs [:radius :rfull])
     :margin        [(gs [:spacing :p0])
                     (gs [:spacing :p4])]}

    :dots-container
    {:padding [(gs [:spacing :p4])
               (gs [:spacing :p12])]}

    :dots-label
    {:margin-right (gs [:spacing :p8])}

    :items-center
    {:display     :flex
     :align-items :center}

    :tag-group-label
    {:color (gs [:colors :gray-scale-400])
     :white-space :nowrap}

    :tag-group-value
    {:margin [(gs [:spacing :p0])
              (gs [:spacing :p8])
              (gs [:spacing :p0])
              (gs [:spacing :p4])]}

    :metadata-grid
    {:display :flex
     :gap (gs [:spacing :p12])}

    :metadata-item-sites
    {:display :flex
     :flex-direction :column
     :width "360px"}

    :metadata-item-roles
    {:display :flex
     :flex-direction :column
     :width "180px"}

    :metadata-item-date
    {:display :flex
     :flex-direction :column
     :width "120px"}

    :table-wrapper
    {:padding [(gs [:spacing :p8])
               (gs [:spacing :p20])
               (gs [:spacing :p20])]}

    :text-padding
    {:padding [(gs [:spacing :p0])
               (gs [:spacing :p4])]}

    :table-container
    {:width "100%"

     "& td, th"
     {:padding [(gs [:spacing :p4])
                (gs [:spacing :p8])]}}

    :table-head
    {:text-align :center

     "& th > *"
     {:margin (gs [:spacing :p0])}}

    :table-body
    {:text-align :center
     :border     (str "1px solid " (gs [:colors :gray-scale-200]))

     "& td"
     {:vertical-align :middle
      :border         (str "1px solid " (gs [:colors :gray-scale-200]))}}}))

(defn- dot [score]
  [:div
   {:class (:dot classes)
    :style {:background-color
            (if (number? score)
              (nth kc/readiness-scale-spectrum (dec (j/call js/Math :round score)))
              (gs [:colors :gray-scale-200]))}}])

(defn- dots [{:keys [label activities]}]
  [:div {:class [(:items-center classes)
                 (:dots-container classes)]}
   [text/caption-30 :span {:class (:dots-label classes)} label]
   [:ul {:class (:items-center classes)}
    (for [{uuid  :activity/uuid
           score :score} activities]
      [:li {:key uuid}
       [dot score]])]])

(defn- learner-header [{:keys [user]}]
  (let [{:user/keys [family-name given-name email-address uuid]
         :curriculum/keys [registration]
         :curriculum.learner/keys [started-at completed-at]
         :curriculum.session/keys [activities]} user]
  [:div {:class (:items-center classes)
         :style {:width "100%"}}
   [:div {:style {:flex-grow 1}}
    [:div {:style {:display :flex}}
     [:div {:style {:width "4%"
                    :margin-right (gs [:spacing :p4])}}
      (if (some? completed-at)
        [icon/icon-sm
         {:style {:color (gs [:colors :chart-green])}
          :title "All required activities complete"
          :data-test (str "learner-status-icon-" uuid "-complete")}
         :check-circle]
        [icon/icon-sm
         {:style {:color (gs [:colors :chart-orange])}
          :title "Some required activities incomplete"
          :data-test (str "learner-status-icon-" uuid "-incomplete")}
         :warning-triangle])]
     [text/title-20 :span (let [full-name     (str/join " " [given-name family-name])]
                            (str " " (some #(when-not (str/blank? %) %) [full-name email-address uuid])))]]
    [:div {:class (:metadata-grid classes)}
     (map-indexed
      (fn [idx {tag-group :content-provider.tag-group/name
                tag :content-provider.tag/name}]
        [:div {:class (if (zero? idx)
                        (:metadata-item-sites classes)
                        (:metadata-item-roles classes))
               :key   tag-group}
         [text/caption-30 :span {:class (:tag-group-label classes)} (str tag-group ": ")]
         [text/caption-30 :span {:class (:tag-group-value classes)} tag]])
      registration)
     [:div {:class (:metadata-item-date classes)}
      [text/caption-30 :span {:class (:tag-group-label classes)} "Started: "]
      [text/caption-30 :span {:class (:tag-group-value classes)}
       (if started-at (ku/date->day-string started-at) "-")]]
     [:div {:class (:metadata-item-date classes)}
      [text/caption-30 :span {:class (:tag-group-label classes)} "Completed: "]
      [text/caption-30 :span {:class (:tag-group-value classes)
                              :data-test (str "learner-completed-at-" uuid)}
       (if completed-at (ku/date->day-string completed-at) "-")]]]]

   [:div {:class (:items-center classes)}
    [:div {:style {:border-right (str "3px solid " (gs [:colors :gray-scale-300]))}}
     [dots {:label "Readiness"
            :activities
            (->> activities
                 (map #(set/rename-keys % {:activity-session/readiness :score})))}]]
    [dots {:label "Mindset"
           :activities
           (->> activities
                (map (fn [a] (update a :activity-session/mindset #(case %
                                                                    :low    1
                                                                    :medium 5
                                                                    :high   10
                                                                    nil     nil))))
                (map #(set/rename-keys % {:activity-session/mindset :score})))}]]]))


(defn- learner-activities
  [{:keys [make-new-query-params
           title
           subtitle
           message
           headers
           hide-headers?
           section-slug
           activities]
    user-uuid       :user/uuid
    curriculum-uuid :curriculum/uuid}]
  [:div (cond-> {:class (:table-wrapper classes)}
          section-slug (assoc :data-test (str "activities-section-" section-slug "-" user-uuid)))
   [:table {:class [(:table-container classes)]}
    [:thead {:class [(:table-head classes)]}
     [:tr
      [:th {:style {:text-align :left}}
       [:p (cond-> {:class (:title classes)}
             section-slug (assoc :data-test (str "activities-section-title-" section-slug "-" user-uuid)))
        title]
       [:p {:class (:subtitle classes)} subtitle]]
      (for [header headers]
        ^{:key header}
        [:th {:style {:width "100px"}}
         (when-not hide-headers?
           [text/caption-30 :p header])])
      [:th {:style {:width "140px"}}]]]

    [:tbody
     (for [{:activity-session/keys [started-at completed-at readiness mindset pre-test]
            :activity-session.user/keys [readiness-offset]
            activity-uuid :activity/uuid
            is-supplemental? :activity/is-supplemental?
            external-activity-link :activity/external-activity-link
            activity-name :activity/name} (sort-by :activity/is-supplemental? activities)
           :let
           [started?
            (some? started-at)

            has-activity-data?
            (and readiness mindset)

            has-tested-out?
            (and
             (true? (:questionnaire.attempts/has-tested-out? pre-test))
             (not has-activity-data?))

            learner-report-link
            (str (aru/admin-path :curriculum-learner-report
                                 :curriculum-uuid curriculum-uuid)
                 "?" (map->query
                      (make-new-query-params
                       #(merge % {:learner-uuid user-uuid
                                  :activity-uuid activity-uuid}))))]]
       [:tr {:key (str "activity" activity-uuid "-" user-uuid)
             :class [(:table-body classes)]}
        [:td (cond-> {:style {:text-align :left}}
               (false? started?)
               (assoc :colSpan 6))
         [:div {:class (:items-center classes)
                :style {:justify-content :space-between}}
          [:div {:class [(:items-center classes)
                         (:text-padding classes)]}
           (when is-supplemental?
             [:span {:class (:is-supplemental classes)
                     :data-test (str "activity-optional-badge-" user-uuid "-" activity-uuid)}
              "Optional"])
           (if (and started-at readiness mindset)
             [link/link {:href learner-report-link} activity-name]
             [text/body-20 :p {} activity-name])]
          (when-not started?
            [text/body-20 :p {:style {:font-style :italic}
                              :class (:text-padding classes)}
             (if external-activity-link
               "Readiness and Mindset data not available for external activities"
               (or message "Not started"))])]]
        (when started?
          [:td [text/body-20 :p {:style {:color (gs [:colors :gray-scale-400])}}
                (ku/date->day-string started-at)]])
        (when started?
          [:td [text/body-20 :p {:style {:color (gs [:colors :gray-scale-400])}}
                (if completed-at (ku/date->day-string completed-at) "-")]])
        (when has-activity-data?
          [:<>
           [:td {:style {:text-align :center}}
            (when (number? readiness)
              [:div {:style {:display     :inline-flex
                             :align-items :center}}
               [dot readiness]
               [text/title-30 :p (j/call readiness :toFixed 1)]
               (when (number? readiness-offset)
                 [diff-badge/render {:diff readiness-offset}])])]
           [:td {:style {:text-align :center}}
            (when mindset
              [:div {:style {:display     :inline-flex
                             :align-items :center}}
               [text/title-20 :p (-> mindset name str/capitalize)]])]
           [:td
            [link/primary-anchor-button
             {:href  learner-report-link
              :style {:margin "0 auto"
                      :width "100%"}}
             "View Report"]]])

        (when (or has-tested-out?
                  (and started? (not has-activity-data?)))
          [:td {:colSpan 4
                :style {:text-align :right}}
           [text/body-20 :p {:style {:font-style :italic}
                             :class (:text-padding classes)}
            (cond
              has-tested-out? "Learner has Tested Out"
              completed-at "Completed without Readiness or Mindset data"
              :else "Started, but not completed")]])])]]])

(defn pagination
  [{:keys      [make-new-query-params  curriculum-uuid context]
    {:curriculum.starters.page-info/keys [total-count limit]} :page-info}]
  (let [{:strs [current-page] :or {current-page "0"}} @(re-frame/subscribe [:router/route-query])
        page-size limit
        current-page (js/parseInt current-page)]
    [:div {:style {:display     "grid"
                   :place-items "center"
                   :margin      "10px"}}
     [pagination-controls/render-results {:current-page current-page :page-size page-size :total-items total-count}]
     [pagination-controls/render {:button    button/secondary-button
                                  :on-change (fn [current-page]
                                               (let [new-query-params (make-new-query-params #(merge % {:current-page current-page}))
                                                     new-context      (assoc context :query new-query-params)]
                                                 (re-frame/dispatch [:router/nav-to
                                                                     (str (aru/admin-path :curriculum-reports
                                                                                          :curriculum-uuid curriculum-uuid)
                                                                          "?" (map->query new-query-params))])
                                                 (re-frame/dispatch [::curriculum-reports/async-fetch-learners-request-id
                                                                     new-context])))}
      {:current-page current-page :total-pages (j/call js/Math :ceil (/ total-count page-size))}]]))

(defn render
  [{:keys [users curriculum-uuid make-new-query-params using-tags? tag-group-names] :as props}]
  [:<>
     [:div {:class (:container classes)}
      [:div {:style {:position :relative}}
       [:ul {:class (:learner-items classes)}
        (for [{:user/keys [uuid]
               :curriculum.session/keys [activities]
               :as user} users
              :let [user (assoc user
                                :curriculum/uuid curriculum-uuid
                                :make-new-query-params make-new-query-params)

                    {assigned true unassigned false}
                    (group-by :activity.user/has-tags? activities)

                    {unassinged-and-started true unassigned-and-not-started false}
                    (group-by #(-> % :activity-session/started-at some?) unassigned)]]
          ^{:key uuid}
          [:li
           [expander/render
            {:header [learner-header {:user user}]
             :body
             (let [headers ["Started" "Completed" "Readiness" "Mindset"]]
               (if-not using-tags?
                 [learner-activities (assoc user
                                            :title "Activities"
                                            :section-slug "activities"
                                            :headers headers
                                            :activities assigned)]
                 [:<>
                  (->> [{:title "Assigned Activities"
                         :section-slug "assigned"
                         :subtitle (str "The following activities are based on "
                                        (str/join ", " (butlast tag-group-names)) " and " (last tag-group-names) ".")
                         :headers headers
                         :activities assigned}

                        {:title "Previously Assigned Activities"
                         :section-slug "previously-assigned"
                         :subtitle (str "The following activities were taken based on a prior "
                                        (str/join ", " (butlast tag-group-names)) " or " (last tag-group-names) ".")
                         :headers headers
                         :hide-headers? true
                         :activities unassinged-and-started}

                        {:title "Unassigned Activities"
                         :section-slug "unassigned"
                         :subtitle (str "The following activities are not assigned based on current "
                                        (str/join ", " (butlast tag-group-names)) " or " (last tag-group-names) ".")
                         :headers headers
                         :hide-headers? true
                         :message "Not assigned"
                         :activities unassigned-and-not-started}]
                       (filter #(-> % :activities seq))
                       (map (fn [activity]
                              ^{:key (:title activity)}
                              [learner-activities (merge user activity)])))]))}]])]]]
     [pagination (dissoc props :users)]])
